/**
 * streakService.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Tracks daily activity streaks.
 * Source of truth: Supabase `user_streaks` table.
 * localStorage is used as a fast local cache / offline fallback only.
 */
import { supabase, isSupabaseConfigured } from "@/lib/supabaseClient";

interface StreakData {
  currentStreak: number;
  longestStreak: number;
  lastActivityDate: string | null;
  activityDates: string[];
}

const STREAK_CACHE_KEY = "tef_activity_streak";

const DEFAULT_STREAK: StreakData = {
  currentStreak: 0,
  longestStreak: 0,
  lastActivityDate: null,
  activityDates: [],
};

function getCacheData(): StreakData {
  try {
    const raw = localStorage.getItem(STREAK_CACHE_KEY);
    if (!raw) return { ...DEFAULT_STREAK };
    return JSON.parse(raw);
  } catch {
    return { ...DEFAULT_STREAK };
  }
}

function setCacheData(data: StreakData): void {
  localStorage.setItem(STREAK_CACHE_KEY, JSON.stringify(data));
}

function computeStreakFromData(data: StreakData): number {
  if (!data.lastActivityDate) return 0;
  const todayStr = new Date().toISOString().split("T")[0];
  if (data.lastActivityDate === todayStr) return data.currentStreak;
  const lastDate = new Date(data.lastActivityDate + "T00:00:00");
  const today = new Date(todayStr + "T00:00:00");
  const diffDays = Math.round((today.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24));
  return diffDays > 1 ? 0 : data.currentStreak;
}

async function getCurrentUserId(): Promise<string | null> {
  if (!isSupabaseConfigured || !supabase) return null;
  try {
    const { data } = await supabase.auth.getUser();
    return data?.user?.id ?? null;
  } catch {
    return null;
  }
}

export const streakService = {
  /**
   * Returns streak data from Supabase (with localStorage cache fallback).
   */
  async getStreakDataAsync(): Promise<StreakData> {
    const userId = await getCurrentUserId();
    if (!userId || !isSupabaseConfigured || !supabase) {
      return getCacheData();
    }

    try {
      const { data, error } = await supabase
        .from("user_streaks")
        .select("current_streak, longest_streak, last_activity_date, activity_dates")
        .eq("user_id", userId)
        .maybeSingle();

      if (error || !data) {
        return getCacheData();
      }

      const streakData: StreakData = {
        currentStreak: data.current_streak ?? 0,
        longestStreak: data.longest_streak ?? 0,
        lastActivityDate: data.last_activity_date ?? null,
        activityDates: Array.isArray(data.activity_dates) ? data.activity_dates : [],
      };

      // Update local cache
      setCacheData(streakData);
      return streakData;
    } catch {
      return getCacheData();
    }
  },

  /**
   * Synchronous read from localStorage cache (for components that can't await).
   * Use getStreakDataAsync() when possible for accuracy.
   */
  getStreakData(): StreakData {
    return getCacheData();
  },

  getCurrentStreak(): number {
    return computeStreakFromData(getCacheData());
  },

  getLastActivityDate(): string | null {
    return getCacheData().lastActivityDate;
  },

  /**
   * Registers today as an activity day.
   * Writes to Supabase and updates local cache.
   */
  async registerActivity(): Promise<void> {
    const todayStr = new Date().toISOString().split("T")[0];
    const cache = getCacheData();

    // Fast path: already registered today, nothing to do
    if (cache.lastActivityDate === todayStr) return;

    // Compute new streak value
    let newStreak = 1;
    if (cache.lastActivityDate) {
      const lastDate = new Date(cache.lastActivityDate + "T00:00:00");
      const today = new Date(todayStr + "T00:00:00");
      const diffDays = Math.round((today.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24));
      if (diffDays === 1) {
        newStreak = (cache.currentStreak || 0) + 1;
      }
    }

    const newDates = Array.isArray(cache.activityDates) ? [...cache.activityDates] : [];
    if (!newDates.includes(todayStr)) newDates.push(todayStr);

    const updatedData: StreakData = {
      currentStreak: newStreak,
      longestStreak: Math.max(newStreak, cache.longestStreak || 0),
      lastActivityDate: todayStr,
      activityDates: newDates,
    };

    // Write to local cache immediately (so UI updates fast)
    setCacheData(updatedData);
    window.dispatchEvent(new Event("dashboardUpdated"));

    // Write to Supabase in background
    this._persistToSupabase(updatedData).catch((err) =>
      console.warn("[StreakService] Failed to persist to Supabase:", err)
    );
  },

  async _persistToSupabase(data: StreakData): Promise<void> {
    const userId = await getCurrentUserId();
    if (!userId || !isSupabaseConfigured || !supabase) return;

    const payload = {
      user_id: userId,
      current_streak: data.currentStreak,
      longest_streak: data.longestStreak,
      last_activity_date: data.lastActivityDate,
      activity_dates: data.activityDates,
      updated_at: new Date().toISOString(),
    };

    // Upsert by user_id
    const { error } = await supabase
      .from("user_streaks")
      .upsert(payload, { onConflict: "user_id" });

    if (error) {
      console.warn("[StreakService] Supabase upsert error:", error.message);
    }
  },
};
